import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
// import { motion, useInView } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Image imports
import itSolution1 from "../images/it-solutions-1.jpg";
import itSolution2 from "../images/it-solutions-2.jpg";
import itSolution3 from "../images/it-solutions-3.jpg";

import innovation1 from "../images/innovation-1.jpg"
import innovation2 from "../images/innovation-2.jpg"
import innovation3 from "../images/innovation-3.jpg"

import equality1 from "../images/equality-growth-1.jpg"
import equality2 from "../images/equality-growth-2.jpg"
import equality3 from "../images/equality-growth-3.jpg"

import environment1 from "../images/it-environment-1.jpg"
import environment2 from "../images/it-environment-2.jpg"
import environment3 from "../images/it-environment-3.jpg"




const HoverBox = ({ title, description, isHovered, onHover, images }) => {
  const { ref, inView } = useInView({ triggerOnce: true });

  return (
    <Box
      ref={ref}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(null)}
      sx={{
        scrollMarginTop: "100px",
        position: "relative",
        width: { xs: "90vw", sm: 300 },
        p: 4,
        m: { xs: 3, sm: 2 },
        mt: { xs: 4, sm: 2 },
        mb: { xs: 6, sm: 4 },
        top: 80,
        textAlign: "center",
        bgcolor: isHovered ? "black" : "white",
        color: isHovered ? "white" : "black",
        // transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: 3,
        boxShadow: 3,
        minHeight: { xs: 320, sm: 280 },
        overflow: "visible",
        zIndex: isHovered ? 1 : 0,

        // ✨ Animation style
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0px)" : "translateY(60px)",
        transitionDelay: inView ? "0.2s" : "0s",
        transition: "all 0.7s ease-in-out",
      }}
    >
      {/* Bubble images */}
      {images.map((img, idx) => (
        <Box
          key={idx}
          component="img"
          src={img.src}
          alt={`img-${idx}`}
          sx={{
            position: "absolute",
            top: img.top ?? "auto",
            bottom: img.bottom ?? "auto",
            left: img.left ?? "auto",
            width: {
              xs: `clamp(100px, ${img.width * 0.6}px, 80vw)`, // Minimum 100px, maximum 80vw
              sm: `clamp(${img.width * 0.8}px, ${img.width}px, 60vw)`,
              md: `${img.width}px`,
            },
            height: "auto", // Let height adjust automatically
            aspectRatio: "1/1", // Force circular aspect ratio
            maxWidth: "100%", // Prevent overflow
            borderRadius: "50%",
            border: "2px solid white",
            objectFit: "cover", // Ensure image fills container
            transform: isHovered
              ? "translate(-50%, -50%) scale(1)"
              : "translate(-50%, -50%) scale(0.5)",
            opacity: isHovered ? 1 : 0,
            transition: isHovered
              ? `all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${
                  idx * 150
                }ms`
              : `all 0.3s ease ${(images.length - idx - 1) * 100}ms`,
            zIndex: 2,
          }}
        />
      ))}

      {/* Text Content */}
      <Box
        sx={{
          ml: 0,
          transition: "transform 0.3s ease 100ms",
          transform: isHovered ? "scale(1.03)" : "scale(1)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, mr: 4, textAlign: "center" }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const AboutEffectBox = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // const headingRef = useRef(null);
  const { ref, inView } = useInView({
    triggerOnce: true,
  });
  const imageSets = [
    [
      {
        src: itSolution1,
        top: -70,
        left: "60%",
        width: 120,
        height: 120,
      },
      {
        src: itSolution2,
        top: "80%",
        left: 40,
        width: 130,
        height: 130,
      },
      {
        src: itSolution3,
        bottom: -130,
        left: "80%",
        width: 150,
        height: 150,
      },
    ],
    [
      {
        src: environment1,
        top: -50,
        left: "50%",
        width: 130,
        height: 130,
      },
      {
        src: environment2,
        bottom: -80,
        left: "20%",
        width: 150,
        height: 150,
      },
      {
        src: environment3,
        bottom: -70,
        left: "90%",
        width: 100,
        height: 100,
      },
    ],
    [
      {
        src: innovation1,
        top: "85%",
        left: "50%",
        width: 150,
        height: 150,
      },
      {
        src: innovation2,
        top: -10,
        left: 50,
        width: 80,
        height: 80,
      },
      {
        src: innovation3,
        top: 135,
        left: "95%",
        width: 90,
        height: 90,
      },
    ],
    [
      {
        src: equality1,
        bottom: -70,
        left: "90%",
        width: 100,
        height: 100,
      },
      {
        src: equality2,
        top: -10,
        left: 50,
        width: 80,
        height: 80,
      },
      {
        src: equality3,
        top: "80%",
        left: 40,
        width: 100,
        height: 100,
      },
    ],
  ];

  const boxData = [
    {
      title: "WHO WE ARE",
      description:
        "We are a trusted IT services provider with a strong presence across APAC, delivering innovative solutions tailored to diverse business needs.",
    },
    {
      title: "WHAT WE DO",
      description:
        "Our expertise spans software development, digital transformation and cloud solutions —empowering clients to stay ahead in the digital era",
    },
    {
      title: "OUR STRENGTH",
      description:
        "With a team of skilled professionals and a customer-centric approach, we ensure high-quality service delivery, scalability, and operational efficiency",
    },
    {
      title: "EXCELLENCE REDEFINED",
      description:
        "Where quality, innovation, and agility come together to deliver unmatched IT solutions. With every project, we aim to not just meet expectations, but to exceed them — redefining what excellence means in the digital era.",
    },
  ];

  return (
    <Box>
      <Box
        ref={ref}
        sx={{
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0px)" : "translatex(-40px)",
          transition: "all 0.6s ease-in-out",
          textAlign: "center",
          mb: 6,
          mt: 8,
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{ fontFamily: "Aeonik-bold", fontWeight: 800 }}
        >
          About Us
        </Typography>
      </Box>

      {/* Bubble Boxes */}
      <Box
        display="flex"
        justifyContent="center"
        flexWrap="wrap"
        sx={{
          gap: { xs: 6, sm: 1 },
          mb: 25,
        }}
      >
        {boxData.map((data, index) => (
          <HoverBox
            key={index}
            {...boxData[index]}
            isHovered={hoveredIndex === index}
            onHover={(val) => setHoveredIndex(val ? index : null)}
            images={imageSets[index]}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AboutEffectBox;
